import { useEffect, useState, useMemo } from 'react';
import { Input, Modal, Form, Select } from 'antd';
import TableComponent from '../../../components/common/TableComponent';
import { Popover, Row } from 'antd';
import { PencilFill, Plus, ThreeDotsVertical, Trash } from 'react-bootstrap-icons';
import { ActionMenu } from '../../../components/common/ActionMenu';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { XSquare } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import {
  getEstablishmentList,
  createEstablishment,
  updateEstablishment,
  deleteEstablishment,
  getEstablishmentTypeList,
} from '../getData';
import { useLocations } from '../../../hooks/useReferenceData';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';

const EstablishmentTable = () => {
  const { t, i18n } = useTranslation(['administration']);
  const { isOnline } = useIsOnlineContext();
  const [dataList, setDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isShowDeleteConfirmation, setIsShowDeleteConfirmation] = useState<boolean>(false);
  const [isShowEditConfirmation, setIsShowEditConfirmation] = useState<boolean>(false);
  const [editValues, setEditValues] = useState<any>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const { successNotification, errorNotification } = useNotificationContext();
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');

  const [total, setTotal] = useState<number>(0);
  const [paginationInfo, setPaginationInfo] = useState<{ page: number; limit: number }>({
    page: 1,
    limit: 10,
  });

  const { locations } = useLocations();
  const [establishmentTypes, setEstablishmentTypes] = useState<any[]>([]);
  const departmentItems = useMemo(
    () =>
      Array.from(new Set((Array.isArray(locations) ? locations : []).map((l: any) => l.district)))
        .filter(Boolean)
        .sort((a: any, b: any) => a.localeCompare(b)),
    [locations]
  );

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<string | undefined>();
  const [filterDepartment, setFilterDepartment] = useState<string | undefined>();
  const [filterMunicipality, setFilterMunicipality] = useState<string | undefined>();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPaginationInfo((prev) => (prev.page !== 1 ? { ...prev, page: 1 } : prev));
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const filteredMunicipalities = useMemo(() => {
    return (Array.isArray(locations) ? locations : [])
      .filter((l: any) => (filterDepartment ? l.district === filterDepartment : true))
      .sort((a: any, b: any) => (a.city || '').localeCompare(b.city || ''));
  }, [locations, filterDepartment]);

  const modalMunicipalities = useMemo(() => {
    return (Array.isArray(locations) ? locations : [])
      .filter((l: any) => l.district === selectedDepartment)
      .sort((a: any, b: any) => (a.city || '').localeCompare(b.city || ''));
  }, [locations, selectedDepartment]);

  const columns = [
    {
      title: t('establishmentId', 'Establishment ID'),
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('type', 'Type'),
      dataIndex: 'establishmentType',
      key: 'establishmentType',
      render: (establishmentType: any) => {
        if (typeof establishmentType === 'object')
          return (
            (i18n.language === 'es' ? establishmentType?.nameEs : establishmentType?.nameEn) || '-'
          );
        return establishmentType || '-';
      },
    },
    {
      title: t('department', 'Department'),
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: t('municipality', 'Municipality'),
      dataIndex: 'municipality',
      key: 'municipality',
    },
    {
      title: t('actions', 'Actions'),
      key: 'actions',
      align: 'center',
      render: (_: any, record: any) => {
        const menuItems = [
          {
            text: t('edit', 'Edit'),
            icon: <PencilFill size={16} className="text-primary-blue-icon" />,
            click: () => handleEdit(record),
          },
          {
            text: t('delete', 'Delete'),
            icon: <Trash size={16} className="text-primary-blue-icon" />,
            click: () => handleDelete(record.id),
          },
        ];

        return (
          <Popover
            content={<ActionMenu menuItems={menuItems} />}
            className="cursor-pointer"
            placement="bottom"
            trigger="hover"
            overlayStyle={{ zIndex: 800 }}
          >
            <Row justify="center">
              <ThreeDotsVertical size={15} />
            </Row>
          </Popover>
        );
      },
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getEstablishmentList({
        page: paginationInfo.page,
        limit: paginationInfo.limit,
        searchQuery: debouncedSearchQuery || undefined,
        establishmentTypeId: filterType || undefined,
        department: filterDepartment || undefined,
        locationId: filterMunicipality || undefined,
      });
      setDataList(response?.data || []);
      setTotal(response?.total || 0);
    } catch (error) {
      errorNotification(t('failedToLoadEstablishments', 'Failed to load establishments') as string);
    }
    setLoading(false);
  };

  const fetchTypes = async () => {
    try {
      const types = await getEstablishmentTypeList();
      setEstablishmentTypes(types || []);
    } catch (error) {}
  };

  useEffect(() => {
    fetchData();
  }, [paginationInfo, debouncedSearchQuery, filterType, filterDepartment, filterMunicipality]);

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleEdit = (record: any) => {
    setEditingItem(record);
    const loc = (Array.isArray(locations) ? locations : []).find(
      (l: any) => l.id === record.locationId
    );
    setSelectedDepartment(loc?.district || '');
    form.setFieldsValue({
      name: record.name,
      department: loc?.district || '',
      locationId: record.locationId,
      establishmentTypeId: record.establishmentTypeId,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    setDeleteItemId(id);
    setIsShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;
    try {
      await deleteEstablishment(deleteItemId);
      await referenceDataSyncService.forceSyncEntity('establishments');
      successNotification(
        t('establishmentDeletedSuccessfully', 'Establishment deleted successfully') as string
      );
      fetchData();
      setIsShowDeleteConfirmation(false);
      setDeleteItemId(null);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message;
      if (errorMsg) {
        errorNotification(t(errorMsg, errorMsg) as string);
      } else {
        errorNotification(
          t('failedToDeleteEstablishment', 'Failed to delete establishment') as string
        );
      }
    }
  };

  const cancelDelete = () => {
    setIsShowDeleteConfirmation(false);
    setDeleteItemId(null);
  };

  const deleteConfirmationHeaderContent = () => {
    return (
      <label className="text-primary-font-gray my-6 px-12 text-center text-xl font-bold">
        {t('deleteEstablishmentConfirmation', 'Delete Establishment')}
      </label>
    );
  };

  const executeModalSubmit = async (values: any) => {
    try {
      if (editingItem) {
        await updateEstablishment(editingItem.id, values);
        successNotification(
          t('establishmentUpdatedSuccessfully', 'Establishment updated successfully') as string
        );
      } else {
        await createEstablishment(values);
        successNotification(
          t('establishmentCreatedSuccessfully', 'Establishment created successfully') as string
        );
      }
      await referenceDataSyncService.forceSyncEntity('establishments');
      setIsModalVisible(false);
      form.resetFields();
      setSelectedDepartment('');
      setEditingItem(null);
      fetchData();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message;
      if (errorMsg) {
        errorNotification(t(errorMsg, errorMsg) as string);
      } else {
        errorNotification(t('errorSaving', 'Error saving establishment') as string);
      }
    }
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem && editingItem.hasPrices) {
        setEditValues(values);
        setIsShowEditConfirmation(true);
      } else {
        await executeModalSubmit(values);
      }
    } catch (error) {
      // Form validation failed, ignore
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedDepartment('');
    setEditingItem(null);
  };

  const onPaginationChanged = (page: number, limit: number) => {
    setPaginationInfo({ page, limit });
  };

  const showModal = () => {
    setSelectedDepartment('');
    form.resetFields();
    setIsModalVisible(true);
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="flex w-full flex-col gap-4 sm:flex-row md:w-auto">
          <Select
            placeholder={t('filterByTypePlaceholder', 'Filter by Type...')}
            value={filterType}
            onChange={(val) => {
              setFilterType(val);
              setPaginationInfo((prev) => ({ ...prev, page: 1 }));
            }}
            showSearch
            optionFilterProp="children"
            allowClear
            className="w-full sm:w-52"
          >
            {(Array.isArray(establishmentTypes) ? establishmentTypes : []).map((type: any) => (
              <Select.Option key={type.id} value={type.id}>
                {i18n.language === 'es' ? type.nameEs : type.nameEn}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder={t('filterByDepartmentPlaceholder', 'Filter by Department...')}
            value={filterDepartment}
            onChange={(val) => {
              setFilterDepartment(val);
              setFilterMunicipality(undefined);
              setPaginationInfo((prev) => ({ ...prev, page: 1 }));
            }}
            showSearch
            optionFilterProp="children"
            allowClear
            className="w-full sm:w-52"
          >
            {departmentItems.map((dep: any) => (
              <Select.Option key={dep} value={dep}>
                {dep}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder={t('filterByMunicipalityPlaceholder', 'Filter by Municipality...')}
            value={filterMunicipality}
            onChange={(val) => {
              setFilterMunicipality(val);
              setPaginationInfo((prev) => ({ ...prev, page: 1 }));
            }}
            showSearch
            optionFilterProp="children"
            allowClear
            className="w-full sm:w-52"
          >
            {filteredMunicipalities.map((l: any) => (
              <Select.Option key={l.id} value={l.id}>
                {l.city}
              </Select.Option>
            ))}
          </Select>
          <Input
            placeholder={t('searchByNamePlaceholder', 'Search by Name')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className="w-full sm:w-56"
          />
        </div>
        <div className="flex w-full justify-end sm:w-auto">
          <PrimarySquareBtn handleOnClick={showModal} disabled={!isOnline}>
            <div className="flex flex-row items-center gap-2">
              <Plus className="h-6 w-6 text-white" /> {t('addEstablishment')}
            </div>
          </PrimarySquareBtn>
        </div>
      </div>

      <TableComponent
        tableData={dataList}
        columns={columns}
        pageSize={paginationInfo.limit}
        currentPage={paginationInfo.page}
        totalItems={total}
        scroll={{ x: 'max-content' }}
        onPaginationChanged={onPaginationChanged}
        showHeader
        loading={loading}
      />

      <Modal
        title={editingItem ? t('editEstablishment') : t('addEstablishment')}
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        okText={editingItem ? t('update') : t('create')}
        cancelText={t('cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('name')}
            rules={[{ required: true, message: t('pleaseInputName') }]}
          >
            <Input placeholder={t('establishmentName', 'Enter establishment name') as string} />
          </Form.Item>

          <Form.Item
            name="establishmentTypeId"
            label={t('type')}
            rules={[{ required: true, message: t('pleaseSelectType') }]}
          >
            <Select placeholder={t('selectType', 'Select type') as string}>
              {(Array.isArray(establishmentTypes) ? establishmentTypes : []).map((estType) => (
                <Select.Option key={estType.id} value={estType.id}>
                  {i18n.language === 'es' ? estType.nameEs : estType.nameEn}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="department"
            label={t('department')}
            rules={[
              {
                required: true,
                message: t('pleaseSelectDepartment'),
              },
            ]}
          >
            <Select
              placeholder={t('departmentPlaceholder', 'Select department') as string}
              onChange={(value) => {
                setSelectedDepartment(value);
                form.setFieldsValue({ locationId: undefined });
              }}
            >
              {departmentItems.map((dep: any) => (
                <Select.Option key={dep} value={dep}>
                  {dep}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="locationId"
            label={t('municipality')}
            rules={[
              {
                required: true,
                message: t('pleaseSelectMunicipality'),
              },
            ]}
          >
            <Select
              placeholder={t('selectMunicipality', 'Select Municipality')}
              showSearch
              optionFilterProp="children"
              disabled={!selectedDepartment}
            >
              {modalMunicipalities.map((l: any) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.city}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModelComponent
        isOpen={isShowDeleteConfirmation}
        headerContent={deleteConfirmationHeaderContent()}
        content={t(
          'deleteEstablishmentConfirmationMessage',
          'Are you sure you want to delete this establishment?'
        )}
        onClickedCancel={cancelDelete}
        footerAction={{
          confimation: {
            label: t('deleteNow', 'Delete Now'),
            onClick: confirmDelete,
          },
          cancel: {
            label: t('cancel', 'Cancel'),
            onClick: cancelDelete,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
      />

      <ConfirmModelComponent
        isOpen={isShowEditConfirmation}
        headerContent={
          <label className="text-primary-font-gray my-6 px-12 text-center text-xl font-bold">
            {t('warning', 'Warning')}
          </label>
        }
        content={t(
          'editEstablishmentPricesWarning',
          'This establishment has price records attached. Changes to its type or location will affect those records. Are you sure you want to proceed?'
        )}
        onClickedCancel={() => setIsShowEditConfirmation(false)}
        footerAction={{
          confimation: {
            label: t('yes', 'Yes'),
            onClick: () => {
              setIsShowEditConfirmation(false);
              executeModalSubmit(editValues);
            },
          },
          cancel: {
            label: t('no', 'No'),
            onClick: () => setIsShowEditConfirmation(false),
          },
        }}
      />
    </div>
  );
};

export default EstablishmentTable;
